import { useEffect, useState } from "react";

/**
 * Valida fotos de documentos:
 * - blurScore: promedio de magnitud de gradiente (Tenengrad). Mayor = más nítido.
 * - exposure.mean: luminancia promedio [0..255]
 * - exposure.clippedHighlights / clippedShadows: % píxeles >250 y <5
 * - edgesTouchingBorder: % de píxeles “con contenido” en un marco del 3% alrededor
 * - flags: blurry, overexposed, underexposed, cropped
 */
const DocumentImageValidator = ({ file, onResult }) => {
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!file || !file.type?.startsWith("image/")) return;

    const run = async () => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        // --- Normalización de tamaño para nitidez estable ---
        const maxW = 1600;
        const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        // -------- Helpers --------
        const Y = new Float32Array((data.length / 4) | 0); // luminancia
        let mean = 0,
          clippedHi = 0,
          clippedLo = 0;
        for (let i = 0, j = 0; i < data.length; i += 4, j++) {
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2];
          const y = 0.2126 * r + 0.7152 * g + 0.0722 * b; // luminancia perceptual
          Y[j] = y;
          mean += y;
          if (y >= 250) clippedHi++;
          if (y <= 5) clippedLo++;
        }
        const N = Y.length;
        mean /= N;
        const exposure = {
          mean,
          clippedHighlights: (clippedHi / N) * 100,
          clippedShadows: (clippedLo / N) * 100,
        };

        // -------- Nitidez (Tenengrad con Sobel) --------
        const sobel = () => {
          const gx = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
          const gy = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
          let sumMag = 0,
            count = 0;
          const idx = (x, y) => y * w + x;
          for (let y = 1; y < h - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
              let sx = 0,
                sy = 0,
                k = 0;
              for (let yy = -1; yy <= 1; yy++) {
                for (let xx = -1; xx <= 1; xx++) {
                  const p = Y[idx(x + xx, y + yy)];
                  sx += gx[k] * p;
                  sy += gy[k] * p;
                  k++;
                }
              }
              const mag = sx * sx + sy * sy;
              sumMag += mag;
              count++;
            }
          }
          return sumMag / count;
        };
        const blurScore = sobel();

        // -------- Bordes recortados --------
        const margin = Math.max(2, Math.floor(Math.min(w, h) * 0.03));
        const isContent = (x, y) => {
          let gsum = 0,
            c = 0;
          for (let yy = -1; yy <= 1; yy++) {
            for (let xx = -1; xx <= 1; xx++) {
              const X1 = Math.min(w - 1, Math.max(0, x + xx));
              const Y1 = Math.min(h - 1, Math.max(0, y + yy));
              const X2 = Math.min(w - 1, Math.max(0, x - xx));
              const Y2 = Math.min(h - 1, Math.max(0, y - yy));
              gsum += Math.abs(Y[Y1 * w + X1] - Y[Y2 * w + X2]);
              c++;
            }
          }
          return gsum / c > 5; // heurístico
        };

        let borderSamples = 0,
          borderContent = 0;
        // Top & Bottom
        for (let y = 0; y < margin; y++) {
          for (let x = 0; x < w; x++) {
            borderSamples++;
            if (isContent(x, y)) borderContent++;
          }
        }
        for (let y = h - margin; y < h; y++) {
          for (let x = 0; x < w; x++) {
            borderSamples++;
            if (isContent(x, y)) borderContent++;
          }
        }
        // Left & Right
        for (let y = margin; y < h - margin; y++) {
          for (let x = 0; x < margin; x++) {
            borderSamples++;
            if (isContent(x, y)) borderContent++;
          }
          for (let x = w - margin; x < w; x++) {
            borderSamples++;
            if (isContent(x, y)) borderContent++;
          }
        }
        const edgesTouchingBorder = (borderContent / borderSamples) * 100;

        // --- Flags "rápidos" (se recalibran en el gate) ---
        const blurry = blurScore < 800;
        const overexposed =
          exposure.mean > 220 || exposure.clippedHighlights > 2.5;
        const underexposed = exposure.mean < 35 || exposure.clippedShadows > 8;
        const cropped = edgesTouchingBorder > 6;

        const out = {
          blurScore,
          exposure,
          edgesTouchingBorder,
          flags: { blurry, overexposed, underexposed, cropped },
        };

        setResult(out);
        onResult?.(out);
        URL.revokeObjectURL(img.src);
      };
    };

    run();
  }, [file]);

  return null;
};

export default DocumentImageValidator;
